/**
 * Fleet Transformer
 * Transforms fleet data for UI display
 */

import { FleetSummary, ConnectorStatusBreakdown, ConnectorStatus } from '../types/dashboard-types';

// Colors for connector status chart (OCPP 1.6 complete palette)
const CONNECTOR_STATUS_COLORS: Record<ConnectorStatus | 'Unknown', string> = {
  Available: '#1d4ed8',      // blue-700 - Ready for charging (dark blue)
  Preparing: '#38bdf8',      // sky-400 - Authorization in progress (light blue)
  Charging: '#22c55e',       // green-500 - Active charging session
  Finishing: '#14b8a6',      // teal-500 - Session ending (turquoise)
  SuspendedEV: '#84cc16',    // lime-500 - Vehicle paused charging (light green 1)
  SuspendedEVSE: '#a3e635',  // lime-400 - Station paused charging (light green 2)
  Faulted: '#ef4444',        // red-500 - Error condition
  Unavailable: '#4b5563',    // gray-600 - Out of service (dark grey)
  Reserved: '#a855f7',       // purple-500 - Reserved for user
  Offline: '#9ca3af',        // gray-400 - Offline machines (grey)
  Unknown: '#d1d5db',        // gray-300 - Unknown status (light grey)
};

export class FleetTransformer {
  /**
   * Calculate connector status breakdown for donut chart
   */
  static calculateConnectorBreakdown(summary: FleetSummary): ConnectorStatusBreakdown[] {
    const total = summary.totalConnectors;
    if (total === 0) return [];

    // Complete OCPP 1.6 connector status breakdown (ordered by display priority)
    const breakdown: ConnectorStatusBreakdown[] = [
      {
        status: 'Available',
        count: summary.availableConnectors,
        percentage: (summary.availableConnectors / total) * 100,
        color: CONNECTOR_STATUS_COLORS.Available,
      },
      {
        status: 'Preparing',
        count: summary.preparingConnectors,
        percentage: (summary.preparingConnectors / total) * 100,
        color: CONNECTOR_STATUS_COLORS.Preparing,
      },
      {
        status: 'Charging',
        count: summary.chargingConnectors,
        percentage: (summary.chargingConnectors / total) * 100,
        color: CONNECTOR_STATUS_COLORS.Charging,
      },
      {
        status: 'SuspendedEV',
        count: summary.suspendedEVConnectors,
        percentage: (summary.suspendedEVConnectors / total) * 100,
        color: CONNECTOR_STATUS_COLORS.SuspendedEV,
      },
      {
        status: 'SuspendedEVSE',
        count: summary.suspendedEVSEConnectors,
        percentage: (summary.suspendedEVSEConnectors / total) * 100,
        color: CONNECTOR_STATUS_COLORS.SuspendedEVSE,
      },
      {
        status: 'Finishing',
        count: summary.finishingConnectors,
        percentage: (summary.finishingConnectors / total) * 100,
        color: CONNECTOR_STATUS_COLORS.Finishing,
      },
      {
        status: 'Reserved',
        count: summary.reservedConnectors,
        percentage: (summary.reservedConnectors / total) * 100,
        color: CONNECTOR_STATUS_COLORS.Reserved,
      },
      {
        status: 'Faulted',
        count: summary.faultedConnectors,
        percentage: (summary.faultedConnectors / total) * 100,
        color: CONNECTOR_STATUS_COLORS.Faulted,
      },
      {
        status: 'Unavailable',
        count: summary.unavailableConnectors,
        percentage: (summary.unavailableConnectors / total) * 100,
        color: CONNECTOR_STATUS_COLORS.Unavailable,
      },
      {
        status: 'Offline',
        count: summary.offlineConnectors,
        percentage: (summary.offlineConnectors / total) * 100,
        color: CONNECTOR_STATUS_COLORS.Offline,
      },
    ];

    // Show all statuses even if count is 0
    return breakdown.map(b => ({
      ...b,
      percentage: Math.round(b.percentage * 10) / 10,
    }));
  }

  /**
   * Get color for connector status
   */
  static getConnectorStatusColor(status: ConnectorStatus): string {
    return CONNECTOR_STATUS_COLORS[status] || CONNECTOR_STATUS_COLORS.Unknown;
  }

  /**
   * Get color for machine status
   */
  static getMachineStatusColor(status: string): string {
    switch (status) {
      case 'Online':
        return '#1d4ed8'; // blue-700 (dark blue)
      case 'Charging':
        return '#22c55e'; // green-500
      case 'Offline':
        return '#9ca3af'; // gray-400
      case 'Faulted':
        return '#ef4444'; // red-500
      default:
        return '#d1d5db'; // gray-300
    }
  }

  /**
   * Format last heartbeat time as relative time
   */
  static formatLastHeartbeat(lastHeartbeat: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - lastHeartbeat.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  }

  /**
   * Calculate machine online percentage
   */
  static calculateOnlinePercentage(summary: FleetSummary): number {
    if (summary.totalMachines === 0) return 0;
    const online = summary.onlineMachines + summary.chargingMachines;
    return Math.round((online / summary.totalMachines) * 100);
  }

  /**
   * Calculate charging percentage
   */
  static calculateChargingPercentage(summary: FleetSummary): number {
    if (summary.totalConnectors === 0) return 0;
    return Math.round((summary.chargingConnectors / summary.totalConnectors) * 100);
  }
}
